import React from 'react';
import {
	defineMessages,
} from 'react-intl';
import {
	useSelector,
} from 'react-redux';

import OldNavigation from './OldNavigation';
import NewNavigation from './NewNavigation';

import {
	buildTechnicalSectionItems,
} from './builders/buildTechnicalSectionItems';

import useAccountId from '~/hooks/useAccountId';
import useEffectiveHomeAccountId from '~/hooks/useEffectiveHomeAccountId';
import useHasMultipleAccounts from '~/hooks/useHasMultipleAccounts';
import useKingdomAdminFeatures from '~/hooks/useKingdomAdminFeatures';
import useMenuExpandedState from '~/hooks/useMenuExpandedState';
import useOpenSwitchAccountModal from '~/hooks/useOpenSwitchAccountModal';
import useViewportType from '~/hooks/useViewportType';
import useWebsiteId from '~/hooks/useWebsiteId';
import useWebsiteAccountName from '~/hooks/useWebsiteAccountName';
import useNavigationLayout from '~/hooks/useNavigationLayout';
import useShouldHideWebsitesToggle from '~/hooks/useShouldHideWebsitesToggle';

import {
	useSelectedAccount,
} from '~/contexts/SelectedAccountContext';

import {
	isPhase3,
} from '~/utilities/viewportSize';

import {
	selectedSectionSelector,
	urlStateSelector,
} from '~/state/ui/selectors';



export const messages = defineMessages({
	account: {
		id: 'ui.menu.account',
	},
	alerts: {
		id: 'ui.menu.alerts',
	},
	community: {
		id: 'ui.menu.community',
	},
	content: {
		id: 'ui.menu.content',
	},
	writingAssistant: {
		id: 'ui.menu.writingAssistant',
		defaultMessage: 'Writing Assistant',
	},
	more: {
		id: 'ui.menu.more',
		defaultMessage: 'More',
	},
	research: {
		id: 'ui.menu.research',
		defaultMessage: 'Research',
	},
	websites: {
		id: 'ui.menu.websites',
		defaultMessage: 'Websites',
	},
	aiSearch: {
		id: 'ui.menu.aiSearch',
		defaultMessage: 'AI Search',
	},
	traditionalSearch: {
		id: 'ui.menu.traditionalSearch',
		defaultMessage: 'Traditional Search',
	},
	dashboard: {
		id: 'ui.menu.dashboard',
	},
	help: {
		id: 'ui.menu.help',
	},
	internal: {
		id: 'ui.menu.internal',
		defaultMessage: 'Internal',
	},
	generalAdmin: {
		id: 'ui.menu.generalAdmin',
		defaultMessage: 'General Admin',
	},
	switchAccount: {
		id: 'ui.menu.switchAccount',
		defaultMessage: 'Switch Account',
	},
	manageMyProfile: {
		id: 'ui.menu.manageMyProfile',
		defaultMessage: 'Manage My Profile',
	},
	home: {
		id: 'ui.menu.home',
	},
	integrations: {
		id: 'ui.menu.integrations',
	},
	issues: {
		id: 'ui.menu.issues',
	},
	notifications: {
		id: 'ui.menu.notifications',
	},
	performance: {
		id: 'ui.menu.performance',
	},
	pages: {
		id: 'ui.menu.pages',
	},
	platform: {
		id: 'ui.menu.platform',
	},
	settings: {
		id: 'ui.menu.settings',
	},
	technical: {
		id: 'ui.menu.technical',
	},
	workspace: {
		id: 'ui.menu.workspace',
	},
});

/**
 * Main Navigation Component (Orchestrator)
 *
 * Orchestrates the navigation system by:
 * - Managing navigation state (expanded sections, hidden items, etc.)
 * - Determining which navigation layout to render (old vs new)
 * - Building shared navigation items (technical section)
 * - Coordinating between OldNavigation and NewNavigation components
 */
const MainNavigation: React.FC = () => {
	const selectedSection = useSelector(selectedSectionSelector);
	const urlState = useSelector(urlStateSelector);
	const accountId = useAccountId();
	const effectiveHomeAccountId = useEffectiveHomeAccountId();
	const hasMultipleAccounts = useHasMultipleAccounts();
	const websiteId = useWebsiteId();
	const websiteAccountName = useWebsiteAccountName(websiteId);
	const [
		currentLayout,
		,
		showToggle,
	] = useNavigationLayout();
	const viewportType = useViewportType();
	const [
		isMenuExpanded,
		,
		isMenuMinimized,
	] = useMenuExpandedState();
	const useNewNavigation = currentLayout === 'new';
	const effectivePhase3 = isPhase3(viewportType, isMenuExpanded, isMenuMinimized);
	const isCurrentlyPhase2 = !effectivePhase3;

	const routeName = urlState?.get ? urlState.get('name') : urlState?.name;

	// Technical area: the five website app sections (dashboard…alerts) + segment editor. Also multiDashboard / home / empty_state —
	// ui/reducer determineActiveSection does not set selectedSection for those routes, so we key off routeName too.
	// Do NOT treat website.detail* as Technical-active: those URLs are the Settings (secondary nav) / website-detail tree;
	// selectedSection is still website.detail there, and WebsiteSettingsLink uses the same for isActive — including detail here caused two “here” indicators.
	const isTechnicalSectionActive = (
		selectedSection === 'website.dashboard'
		|| selectedSection === 'website.pages'
		|| selectedSection === 'website.platform'
		|| selectedSection === 'website.issues'
		|| selectedSection === 'website.alerts'
	) || (
		typeof routeName === 'string' && (
			routeName.startsWith('website.dashboard')
			|| routeName.startsWith('website.pages')
			|| routeName.startsWith('website.platform')
			|| routeName.startsWith('website.issues')
			|| routeName.startsWith('website.alerts')
			|| routeName.startsWith('website.segmentEditor')
			|| routeName.startsWith('multiDashboard')
			|| routeName === 'home'
			|| routeName.startsWith('empty_state')
		)
	);

	// Check if any Account section sub-item is active (for auto-expand)
	const isAccountSectionActive = (
		selectedSection === 'account.websites'
		|| selectedSection === 'account.yourProfile'
		|| selectedSection === 'account.settings'
	);

	// Check if any Internal section sub-item is active (General Admin)
	const isInternalSectionActive = selectedSection === 'generalAdmin'
		|| (typeof selectedSection === 'string' && selectedSection.startsWith('generalAdmin.'));

	// Check if any Content section sub-item is active (for bubble styling)
	const hasContentActiveDescendant = selectedSection === 'writingAssistant' || selectedSection === 'more';

	// Check if any Performance section sub-item is active (for bubble styling)
	const hasPerformanceActiveDescendant = (
		selectedSection === 'research'
		|| selectedSection === 'websites'
		|| selectedSection === 'aiSearch'
		|| selectedSection === 'traditionalSearch'
	);

	const [isTechnicalExpanded, setTechnicalExpanded] = React.useState<boolean>(true);
	const [areTechnicalSubItemsHidden, setAreTechnicalSubItemsHidden] = React.useState<boolean>(false);
	const [areSecondaryNavItemsHidden, setAreSecondaryNavItemsHidden] = React.useState<boolean>(false);

	// Parent gray bubble (data-has-active-descendant): Technical — only when collapsed with any technical-area route,
	// or when expanded and one of the five website nav sub-items (Dashboard…Alerts) is active — not multiDashboard/home/detail alone.
	const isTechnicalWebsiteNavSubItemActive = (
		selectedSection === 'website.dashboard'
		|| selectedSection === 'website.pages'
		|| selectedSection === 'website.platform'
		|| selectedSection === 'website.issues'
		|| selectedSection === 'website.alerts'
	);

	const hasTechnicalActiveDescendant = (
		isTechnicalSectionActive
		&& (!isTechnicalExpanded || isTechnicalWebsiteNavSubItemActive)
	);

	// Content section expanded state
	const [isContentExpanded, setContentExpanded] = React.useState<boolean>(false);

	// Performance section expanded state
	const [isPerformanceExpanded, setPerformanceExpanded] = React.useState<boolean>(false);

	// Internal section expanded state (for kingdom admins)
	const [isInternalExpanded, setInternalExpanded] = React.useState<boolean>(isInternalSectionActive);

	// Account section expanded state
	const [isAccountExpanded, setAccountExpanded] = React.useState<boolean>(isAccountSectionActive);

	// Account: gray parent when collapsed on an account route, or expanded on Manage profile / Settings — not on default account.websites only.
	const hasAccountActiveDescendant = (
		isAccountSectionActive
		&& (!isAccountExpanded
			|| selectedSection === 'account.yourProfile'
			|| selectedSection === 'account.settings')
	);

	// Internal: main click always navigates to General Admin, so the sub-item is active at the same time as the section opens.
	// Use full section-active flag for parent bubble: gray parent + green General Admin row (single green bubble).
	const hasInternalActiveDescendant = isInternalSectionActive;

	const kingdomAdminFeatures = useKingdomAdminFeatures();
	const openSwitchAccountModal = useOpenSwitchAccountModal(true);

	const {
		selectedAccountId,
		setSelectedAccountId,
	} = useSelectedAccount();

	// Calculate if Phase 2 compact layout should be shown for Technical section
	const showPhase2CompactLayout = useNewNavigation
		&& isTechnicalExpanded
		&& isCurrentlyPhase2
		&& !areTechnicalSubItemsHidden
		&& !areSecondaryNavItemsHidden;

	// Reset hidden states whenever Technical section becomes expanded
	// This ensures Secondary Nav Items and Technical Sub-Items are shown when Technical is expanded
	// Using useLayoutEffect to ensure the reset happens synchronously before paint
	// This handles both user-initiated expansion (via toggle) and programmatic expansion (via navigation)
	React.useLayoutEffect(() => {
		if (isTechnicalExpanded) {
			// Reset both hidden states when Technical is expanded
			setAreTechnicalSubItemsHidden(false);
			setAreSecondaryNavItemsHidden(false);
		}
	}, [isTechnicalExpanded]);

	// Track previous state to detect when user navigates TO a Technical section
	const prevIsTechnicalSectionActiveRef = React.useRef<boolean>(isTechnicalSectionActive);

	// Auto-expand Technical section only when navigating TO a Technical section
	// (when isTechnicalSectionActive changes from false to true)
	// Don't force it open if user manually collapsed it
	React.useEffect(() => {
		const wasActive = prevIsTechnicalSectionActiveRef.current;
		const isNowActive = isTechnicalSectionActive;

		// Only auto-expand when transitioning from inactive to active
		// This allows users to manually collapse it even when on a Technical page
		if (!wasActive && isNowActive) {
			setTechnicalExpanded(true);
		}

		// Update ref for next comparison
		prevIsTechnicalSectionActiveRef.current = isNowActive;
	}, [
		isTechnicalSectionActive,
	]);

	// Collapse expandables when entering Phase 2; expand Account/Internal when entering Phase 3 via "Keep open"
	const prevIsCurrentlyPhase2Ref = React.useRef<boolean>(isCurrentlyPhase2);
	React.useEffect(() => {
		const wasPhase2 = prevIsCurrentlyPhase2Ref.current;
		const isNowPhase2 = isCurrentlyPhase2;

		if (!wasPhase2 && isNowPhase2 && useNewNavigation) {
			// Entering Phase 2: collapse all (viewport shrink or user minimizes)
			setContentExpanded(false);
			setPerformanceExpanded(false);
			setInternalExpanded(false);
			setAccountExpanded(false);
		} else if (wasPhase2 && !isNowPhase2 && useNewNavigation) {
			// Entering Phase 3 via "Keep open": expand Account or Internal if on that route
			// Mutually exclusive - expand one, collapse the other (same as onAccountToggle/onInternalToggle)
			if (isAccountSectionActive) {
				setAccountExpanded(true);
				setInternalExpanded(false);
			} else if (isInternalSectionActive) {
				setInternalExpanded(true);
				setAccountExpanded(false);
			}
		}

		prevIsCurrentlyPhase2Ref.current = isNowPhase2;
	}, [
		isCurrentlyPhase2,
		useNewNavigation,
		isAccountSectionActive,
		isInternalSectionActive,
	]);

	// Track previous state to detect when user navigates TO an Account section
	const prevIsAccountSectionActiveRef = React.useRef<boolean>(isAccountSectionActive);

	// Auto-expand Account section when navigating TO account.websites or account.settings
	React.useEffect(() => {
		const wasActive = prevIsAccountSectionActiveRef.current;
		const isNowActive = isAccountSectionActive;

		if (!wasActive && isNowActive) {
			setAccountExpanded(true);
		}

		prevIsAccountSectionActiveRef.current = isNowActive;
	}, [
		isAccountSectionActive,
	]);

	// Track previous state to detect when user navigates TO an Internal section (General Admin)
	const prevIsInternalSectionActiveRef = React.useRef<boolean>(isInternalSectionActive);

	// Auto-expand Internal section when navigating TO General Admin
	React.useEffect(() => {
		const wasActive = prevIsInternalSectionActiveRef.current;
		const isNowActive = isInternalSectionActive;

		if (!wasActive && isNowActive) {
			setInternalExpanded(true);
		}

		prevIsInternalSectionActiveRef.current = isNowActive;
	}, [
		isInternalSectionActive,
	]);

	let activeItemIndex: number | undefined = undefined;

	switch (selectedSection) {

		case 'website.dashboard':
			activeItemIndex = 0;
			break;

		case 'website.pages':
			activeItemIndex = 1;
			break;

		case 'website.platform':
			activeItemIndex = 2;
			break;

		case 'website.issues':
			activeItemIndex = 3;
			break;

		case 'website.alerts':
			activeItemIndex = 4;
			break;

		case 'website.detail':
			// Always show active state in Phase 2 (56px vertical sidebar)
			activeItemIndex = 5;
			break;

		case 'community':
			// Always show active state in Phase 2 (56px vertical sidebar)
			activeItemIndex = 6;
			break;

	}

	const websitesNavToggleRef = React.useRef<HTMLLIElement | null>(null);
	const hideWebsitesToggle = useShouldHideWebsitesToggle();

	const technicalSectionItems = React.useMemo(
		() => buildTechnicalSectionItems({
			hideWebsitesToggle,
			messages,
			websiteAccountName,
			websiteId,
			websitesNavToggleRef,
		}),
		[
			hideWebsitesToggle,
			websiteAccountName,
			websiteId,
			websitesNavToggleRef,
		],
	);


	// Render new navigation structure when toggle is ON
	if (useNewNavigation) {
		return (
			<NewNavigation
				accountId={accountId}
				areSecondaryNavItemsHidden={areSecondaryNavItemsHidden}
				areTechnicalSubItemsHidden={areTechnicalSubItemsHidden}
				effectiveHomeAccountId={effectiveHomeAccountId}
				hasAccountActiveDescendant={hasAccountActiveDescendant}
				hasContentActiveDescendant={hasContentActiveDescendant}
				hasInternalActiveDescendant={hasInternalActiveDescendant}
				hasMultipleAccounts={hasMultipleAccounts}
				hasPerformanceActiveDescendant={hasPerformanceActiveDescendant}
				hasTechnicalActiveDescendant={hasTechnicalActiveDescendant}
				hideWebsitesToggle={hideWebsitesToggle}
				isAccountExpanded={isAccountExpanded}
				isAccountSectionActive={isAccountSectionActive}
				isContentExpanded={isContentExpanded}
				isCurrentlyPhase2={isCurrentlyPhase2}
				isInternalExpanded={isInternalExpanded}
				isInternalSectionActive={isInternalSectionActive}
				isPerformanceExpanded={isPerformanceExpanded}
				isTechnicalExpanded={isTechnicalExpanded}
				isTechnicalSectionActive={isTechnicalSectionActive}
				kingdomAdminFeatures={kingdomAdminFeatures}
				messages={messages}
				openSwitchAccountModal={openSwitchAccountModal}
				selectedAccountId={selectedAccountId}
				setAccountExpanded={setAccountExpanded}
				setAreSecondaryNavItemsHidden={setAreSecondaryNavItemsHidden}
				setAreTechnicalSubItemsHidden={setAreTechnicalSubItemsHidden}
				setContentExpanded={setContentExpanded}
				setInternalExpanded={setInternalExpanded}
				setPerformanceExpanded={setPerformanceExpanded}
				setSelectedAccountId={setSelectedAccountId}
				setTechnicalExpanded={setTechnicalExpanded}
				showPhase2CompactLayout={showPhase2CompactLayout}
				showToggle={showToggle}
				technicalSectionItems={technicalSectionItems}
				websiteAccountName={websiteAccountName}
				websiteId={websiteId}
				websitesNavToggleRef={websitesNavToggleRef}
			/>
		);
	}

	// Old navigation structure (when toggle is OFF)
	return (
		<OldNavigation
			activeItemIndex={activeItemIndex}
			messages={messages}
			websiteId={websiteId}
		/>
	);
};

export default MainNavigation;
